// src/pages/inventory/ManageSupplier.js
import React, { useState, useEffect, Fragment } from "react";
import { Card, Col, Table, Input, CardBody, Button, Media, Label } from "reactstrap";
import { FormLabel, Modal } from "react-bootstrap";
import { Filter, X, Eye } from "react-feather";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { toast } from "react-toastify";
import "react-phone-input-2/lib/style.css";
import { RiFileList3Fill } from "react-icons/ri";

// API functions
import {
	getSuppliersAPI,
	viewSupplierAPI,
	deleteSupplierAPI,
	updateSupplierAPI,
} from "./SupplierService";
import Swal from "sweetalert2";
import { FaPlusCircle } from "react-icons/fa";
import { useNavigate } from "react-router";
import { useCurrency } from "../../_helper/CurrencyContext";
import SupplierModal from "./SupplierModal"; // Import the SupplierModal component

const ManageSupplier = () => {
	const { getCurrencySymbol } = useCurrency();
	const [suppliers, setSuppliers] = useState([]);
	const [page, setPage] = useState(1);
	const [size, setSize] = useState(10);
	const [totalItems, setTotalItems] = useState(0);
	const [searchValue, setSearchValue] = useState("");
	const [statusFilter, setStatusFilter] = useState(""); // active | inactive | all
	const { setLoading } = useLoader();
	const { t } = useTranslation();
	const { pharmacyPOS } = usePharmacy();
	const navigate = useNavigate();

	const [showModal, setShowModal] = useState(false);
	const [isEdit, setIsEdit] = useState(false);
	const [viewData, setViewData] = useState(null);
	const [selectedSupplier, setSelectedSupplier] = useState(null);
	const [statusTooltips, setStatusTooltips] = useState(false);
	const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
	const [viewPenPaymentsTooltipOpen, setViewPenPaymentsTooltipOpen] = useState(false);
	const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
	const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
	const toggleViewPenPaymentsTooltip = () => setViewPenPaymentsTooltipOpen(!viewPenPaymentsTooltipOpen);
	const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);
	const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleStatusTooltip = () => setStatusTooltips(!statusTooltips);

	const [isClearing, setIsClearing] = useState(false);

	// Fetch suppliers
	useEffect(() => {
		getSuppliers();
	}, [page, size]);

	const getSuppliers = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			search: searchValue.trim(),
			status: statusFilter || '',
			page,
			limit: size,
		};
		try {
			setLoading(true);
			const response = await getSuppliersAPI(body);
			setSuppliers(response.data.data.suppliers || []);
			const totalCount = response.data.data.total || 0;
			setTotalItems(Math.ceil(totalCount / size));
		} catch (error) {
			console.error("Error fetching suppliers:", error);
			setSuppliers([]);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = () => {
		setPage(1);
		getSuppliers();
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue("");
		setStatusFilter("");
		// setPage(1);
		// getSuppliers();
	};

	useEffect(() => {
		if (isClearing) {
			getSuppliers();
			setIsClearing(false);
		}
	}, [isClearing]);

	const handlePageChange = (pageNumber) => setPage(pageNumber);

	const handlePageSizeChange = (e) => {
		setSize(parseInt(e.target.value));
		setPage(1);
	};

	// Open Add/Edit Modal using SupplierModal component
	const openModal = (supplier = null) => {
		if (supplier) {
			// Transform supplier data to match SupplierModal format
			const transformedSupplier = {
				companyName: supplier.companyName,
				name: supplier.contactPerson,
				email: supplier.email,
				phone: supplier.phone,
				phoneCode: supplier.countryCode,
				address: supplier.address,
				country: supplier.country || "",
				state: supplier.state || "",
				stateCode: supplier.stateCode || "",
				city: supplier.city || "",
			};
			setSelectedSupplier({ ...supplier, ...transformedSupplier });
			setIsEdit(true);
		} else {
			setSelectedSupplier(null);
			setIsEdit(false);
		}
		setShowModal(true);
	};

	// Close modal
	const closeModal = () => {
		setShowModal(false);
		setSelectedSupplier(null);
	}

	// Handle supplier saved callback
	const handleSupplierSaved = (supplier) => {
		// Refresh supplier list
		getSuppliers();
		closeModal();
	};

	const statusChange = async (_id, event) => {
		event.preventDefault();
		Swal.fire({
			icon: 'warning',
			title: t("Are you sure"),
			text: event.target.checked ? t("you wish to Activate this user?") : t("you wish to Inactive this user?"),
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn-primary',
				cancelButton: 'btn-danger',
			},
			confirmButtonText: event.target.checked ? t("Yes, Activate") : t("Yes, Inactive"),
			cancelButtonText: t("Cancel")
			// reverseButtons: true
		}).then(async (result) => {
			if (result.value) {
				const fd = new FormData();
				fd.append('_id', _id);
				if (!event.target.checked === true) {
					fd.append('status', 'active')
				} else {
					fd.append('status', 'inactive')
				}
				const body = {
					clinicId: pharmacyPOS?.hospital_id,
					supplierId: _id,
					status: (!event.target.checked === true) ? 'active' : 'inactive'
				}

				try {
					setLoading(true);
					// You'll need to update this API call to include the new fields
					// For now, we'll use the existing update API
					const response = await updateSupplierAPI(body);
					if (response.data.status === 'success') {
						getSuppliers();
						toast.success(response.data.message);
						setLoading(false);
					}
				} catch (error) {
					console.error('Error fetching data:', error);
				} finally {
					setLoading(false);
				}
			}
		})
	}

	// View Supplier
	const handleViewClick = async (supplierId) => {
		try {
			setLoading(true);
			const response = await viewSupplierAPI({
				clinicId: pharmacyPOS?.hospital_id,
				supplierId: supplierId,
			});
			setViewData(response.data.data);
		} catch (error) {
			console.error("Error viewing supplier:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleViewPendingPayments = (supplier) => {
		navigate(`${process.env.PUBLIC_URL}/purchase`, {
			state: { supplier }
		});
	};

	// Delete Supplier
	const handleDeleteSupplier = async (supplierId) => {
		const confirm = await Swal.fire({
			title: t("Are you sure?"),
			text: t("You want to delete this supplier?"),
			icon: "warning",
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn-primary',
				cancelButton: 'btn-danger',
			},
			confirmButtonText: t("Yes, Delete It!"),
		});

		if (confirm.isConfirmed) {
			try {
				setLoading(true);
				await deleteSupplierAPI({
					clinicId: pharmacyPOS?.hospital_id,
					supplierId,
				});
				toast.success(t("Supplier deleted successfully"));
				getSuppliers();
			} catch (error) {
				console.error("Error deleting supplier:", error);
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Manage Suppliers")}
				parent={t("Dashboard")}
				title={t("Suppliers")}
			/>

			<Col sm="12">
				{/* Search and Add */}
				<Card>
					<CardBody>
						{/* Search + Filters + Add Supplier */}
						<div className="row g-2">
							<Col sm="4">
								<label className="form-label">{t("Search")}</label>
								<Input
									type="text"
									placeholder={t("Search by company, email, person or phone")}
									value={searchValue}
									onChange={(e) => setSearchValue(e.target.value)}
									onKeyDown={(e) => e.key === "Enter" && handleSearch()}
								/>
							</Col>
							<Col sm="3">
								<label className="form-label">{t("Status")}</label>
								<select
									className="form-control"
									value={statusFilter}
									onChange={(e) => setStatusFilter(e.target.value)}
								>
									<option value="">{t("All")}</option>
									<option value="active">{t("Active")}</option>
									<option value="inactive">{t("Inactive")}</option>
								</select>
							</Col>
							<Col sm="3" className="d-flex align-items-end gap-2">
								<Button id="filter-tooltip" color="primary" onClick={handleSearch}>
									<Filter size={16} />
									<ToolTip
										attrToolTip={{
											placement: 'top',
											isOpen: filterTooltipOpen || false,
											target: `filter-tooltip`,
											toggle: () => toggleFilterTooltip(),
										}} >
										<strong>{t("Filter")}</strong>
									</ToolTip>
								</Button>
								<Button id="clear-tooltip" color="secondary" onClick={handleClear}>
									<X size={16} />
									<ToolTip
										attrToolTip={{
											placement: 'top',
											isOpen: clearTooltipOpen || false,
											target: `clear-tooltip`,
											toggle: () => toggleClearTooltip(),
										}} >
										<strong>{t("Clear")}</strong>
									</ToolTip>
								</Button>
							</Col>
							<div className="col d-flex justify-content-end align-items-end">
								<Button className="d-flex align-items-center gap-2" color="primary" onClick={() => openModal()}>
									<FaPlusCircle size={16} /> {t("Add Supplier")}
								</Button>
							</div>
						</div>
					</CardBody>
				</Card>

				{/* Supplier List */}
				<Card>
					<CardBody>
						<div className="table-responsive">
							<Table bordered hover striped>
								<thead>
									<tr>
										<th>#</th>
										<th>{t("GST Number")}</th>
										<th>{t("Company Name")}</th>
										<th>{t("Contact Person")}</th>
										<th>{t("Email")}</th>
										<th>{t("Phone")}</th>
										<th>{t("Location")}</th>
										<th className="text-center">{t("Status")}</th>
										<th>{t("Outstanding Balance")}</th>
										<th className="text-center">{t("Action")}</th>
									</tr>
								</thead>
								<tbody>
									{suppliers.length > 0 ? (
										suppliers.map((supplier, idx) => (
											<tr key={supplier._id}>
												<td>{(page - 1) * size + idx + 1}</td>
												<td>{supplier.gstNumber || '-'}</td>
												<td>{supplier.companyName || '-'}</td>
												<td>{supplier.contactPerson || '-'}</td>
												<td>{supplier.email || '-'}</td>
												<td>{`${supplier.countryCode || '-'} ${supplier.phone || '-'}`}</td>
												<td>
													{/* Show city, state, country if available */}
													<div>
														{`${supplier.city || ''}${supplier.city && supplier.state ? ', ' : ''}${supplier.state || ''}`}
														{supplier.country && <div className="text-muted small">{supplier.country}</div>}
													</div>
												</td>
												<td>
													<Media body className="text-center icon-state">
														<Label className='switch' id={`status-${supplier?._id}`} htmlFor={`flexSwitchCheck-${supplier._id}`}>
															<Input
																type='checkbox'
																id={`flexSwitchCheck-${supplier._id}`}
																checked={supplier.status === 'active'}
																onChange={(e) => statusChange(supplier._id, e)}
															/>
															<span className='switch-state'></span>
														</Label>
													</Media>
													<ToolTip
														attrToolTip={{
															placement: 'top',
															isOpen: statusTooltips[supplier?._id] || false,
															target: `status-${supplier?._id}`,
															toggle: () => toggleStatusTooltip(supplier?._id),
														}} >
														<strong>{t("Change Status")}</strong>
													</ToolTip>
												</td>
												<td>
													<strong className="text-danger">{getCurrencySymbol()}{supplier.balance.toFixed(2)}</strong>
												</td>
												<td className='text-center text-nowrap'>
													{/* {Number(supplier.balance) > 0 && ( */}
													<button
														className='success-action-button me-1'
														id={`viewPenPaymentsTooltipOpen`}
														onClick={() => handleViewPendingPayments(supplier)}
													>
														<RiFileList3Fill />

														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: viewPenPaymentsTooltipOpen,
																target: `viewPenPaymentsTooltipOpen`,
																toggle: toggleViewPenPaymentsTooltip,
															}}
														>
															<strong>{t("View All Pending Payments")}</strong>
														</ToolTip>
													</button>
													{/* )} */}
													<button className='success-action-button me-1' id="ViewToolTip" onClick={() => handleViewClick(supplier?._id)}>
														<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: viewTooltipOpen,
																target: 'ViewToolTip',
																toggle: toggleViewTooltip,
															}} >
															<strong>{t("View")}</strong>
														</ToolTip>
													</button>
													<button className='success-action-button me-1' id="EditToolTip" onClick={() => openModal(supplier)}>
														<i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: editTooltipOpen,
																target: 'EditToolTip',
																toggle: toggleEditTooltip,
															}} >
															<strong>{t("Edit")}</strong>
														</ToolTip>
													</button>
													<button className='danger-action-button' id="DeleteToolTip" onClick={() => handleDeleteSupplier(supplier?._id)}>
														<i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
														<ToolTip
															attrToolTip={{
																placement: 'top',
																isOpen: deleteTooltipOpen,
																target: 'DeleteToolTip',
																toggle: toggleDeleteTooltip,
															}} >
															<strong>{t("Delete")}</strong>
														</ToolTip>
													</button>
												</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="10" className="text-center text-muted p-3">
												{t("No suppliers found")}
											</td>
										</tr>
									)}
								</tbody>
							</Table>
						</div>

						{/* Pagination */}
						<div className="d-md-flex align-items-center justify-content-between mt-3">
							<div className="show-entries">
								<FormLabel>{t("Show")}&nbsp;</FormLabel>
								<select
									value={size}
									onChange={handlePageSizeChange}
									className="form-control"
									style={{ display: "inline", width: "auto" }}
								>
									<option value={5}>5</option>
									<option value={10}>10</option>
									<option value={20}>20</option>
								</select>
								&nbsp;<FormLabel>{t("entries")}</FormLabel>
							</div>
							<MyPagination
								totalPages={totalItems}
								currentPage={page}
								onPageChange={handlePageChange}
							/>
						</div>
					</CardBody>
				</Card>
			</Col>

			{/* Use SupplierModal component */}
			<SupplierModal
				show={showModal}
				onHide={closeModal}
				clinicId={pharmacyPOS?.hospital_id}
				onSaved={handleSupplierSaved}
				initial={selectedSupplier}
				isEdit={isEdit}
			/>

			{/* View Supplier Modal */}
			<Modal show={!!viewData} onHide={() => setViewData(null)}>
				<Modal.Header closeButton>
					<Modal.Title>{t("Supplier Details")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{viewData ? (
						<div>
							<p><strong>{t("GST Number")}:</strong> {viewData.gstNumber || 'Not Provided'}</p>
							<p><strong>{t("Company Name")}:</strong> {viewData.companyName}</p>
							<p><strong>{t("Contact Person")}:</strong> {viewData.contactPerson || '-'}</p>
							<p><strong>{t("Email")}:</strong> {viewData.email || '-'}</p>
							<p><strong>{t("Phone")}:</strong> {viewData.countryCode || '-'} {viewData.phone || '-'}</p>
							<p><strong>{t("Address")}:</strong> {viewData.address || '-'}</p>
							{viewData.city && <p><strong>{t("City")}:</strong> {viewData.city || '-'}</p>}
							{viewData.state && <p><strong>{t("State")}:</strong> {viewData.state || '-'}</p>}
							{viewData.country && <p><strong>{t("Country")}:</strong> {viewData.country || '-'}</p>}
							{viewData.stateCode && <p><strong>{t("State Code")}:</strong> {viewData.stateCode || '-'}</p>}
							<p><strong>{t("Status")}:</strong> {viewData.status}</p>
							<p><strong>{t("Balance")}:</strong> {getCurrencySymbol()}{viewData.balance?.toFixed(2) || '0.00'}</p>
						</div>
					) : (
						<p>{t("No data available")}</p>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button color="secondary" onClick={() => setViewData(null)}>
						{t("Close")}
					</Button>
				</Modal.Footer>
			</Modal>

		</Fragment>
	);
};

export default ManageSupplier;